/**
 * Megamenu - Accessible drilldown navigation
 *
 * Desktop: Tab-style navigation
 * Mobile: Drilldown navigation with back button
 *
 * WCAG AA compliant:
 * - Proper focus management
 * - Keyboard navigation (Arrow keys, Enter, Space, Escape)
 * - ARIA attributes for screen readers
 */

(function () {
  'use strict';

  const DESKTOP_BREAKPOINT = 1024; // Matches $l breakpoint
  const STORAGE_KEY = 'coronaria-recently-viewed';
  const MAX_RECENT_ITEMS = 10;
  const REST_ENDPOINT = '/wp-json/coronaria/v1/personalized-cards';

  /* =========================================================================
     Cookie Consent
     ========================================================================= */

  /**
   * Check if Cookiebot has granted preferences consent (required for localStorage history).
   * Falls back to true when Cookiebot is not present (e.g. development).
   *
   * @return {boolean}
   */
  function hasPreferencesConsent() {
    if (typeof window.Cookiebot === 'undefined') { return true; }
    return !!(window.Cookiebot.consent && window.Cookiebot.consent.preferences);
  }

  /**
   * Clear stored history from localStorage (called when consent is withdrawn).
   */
  function clearStoredHistory() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // localStorage unavailable
    }
  }

  // Clear stored history and update placeholders if preferences consent is declined or withdrawn.
  window.addEventListener('CookiebotOnDecline', function () {
    if (!hasPreferencesConsent()) {
      clearStoredHistory();
      document.querySelectorAll('.js-megamenu').forEach(function (el) {
        if (el.megamenuInstance) {
          el.megamenuInstance.refreshPlaceholders();
        }
      });
    }
  });

  // Re-evaluate placeholders once Cookiebot has loaded the existing consent state on page load.
  // This fires for returning visitors before any dialog is shown.
  window.addEventListener('CookiebotOnConsentReady', function () {
    document.querySelectorAll('.js-megamenu').forEach(function (el) {
      if (el.megamenuInstance) {
        el.megamenuInstance.refreshPlaceholders();
      }
    });
  });

  /* =========================================================================
     Recently Viewed Tracking
     ========================================================================= */

  /**
   * Get recently viewed items from localStorage.
   *
   * @return {Array} Array of { id, type, timestamp } objects.
   */
  function getRecentlyViewed() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) { return []; }
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Remove an item from recently viewed by post ID.
   *
   * @param {number} postId The post ID to remove.
   */
  function removeRecentlyViewed(postId) {
    if (!hasPreferencesConsent()) { return; }
    var items = getRecentlyViewed();
    items = items.filter(function (item) {
      return item.id !== postId;
    });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // localStorage full or unavailable
    }
  }

  /**
   * Track recently viewed employees and offices in localStorage.
   * Runs on every page load to detect single employee/office pages via body classes.
   */
  function trackRecentlyViewed() {
    if (!hasPreferencesConsent()) { return; }
    var body = document.body;
    if (!body) { return; }

    var classes = body.className;
    var type = null;

    if (classes.indexOf('single-employee') !== -1) {
      type = 'employee';
    } else if (classes.indexOf('single-office') !== -1) {
      type = 'office';
    }

    if (!type) { return; }

    // Extract post ID from body class (WordPress adds postid-{N})
    var match = classes.match(/postid-(\d+)/);
    if (!match) { return; }

    var postId = parseInt(match[1], 10);
    if (!postId) { return; }

    var items = getRecentlyViewed();

    // Remove existing entry for this post (will be re-added at front)
    items = items.filter(function (item) {
      return item.id !== postId;
    });

    // Prepend new item
    items.unshift({
      id: postId,
      type: type,
      timestamp: Date.now(),
    });

    // Cap at max items
    items = items.slice(0, MAX_RECENT_ITEMS);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // localStorage full or unavailable
    }
  }

  // Track on every page load (also re-runs after Cookiebot accepts preferences).
  trackRecentlyViewed();
  window.addEventListener('CookiebotOnAccept', function () {
    if (hasPreferencesConsent()) {
      trackRecentlyViewed();
      document.querySelectorAll('.js-megamenu').forEach(function (el) {
        if (el.megamenuInstance) {
          el.megamenuInstance.refreshPlaceholders();
        }
      });
    }
  });

  /**
   * Megamenu controller class
   */
  class Megamenu {
    constructor(element) {
      this.menu = element;
      this.sections = element.querySelectorAll('.megamenu__section-btn');
      this.panels = element.querySelectorAll('.megamenu__panel');
      this.backButtons = element.querySelectorAll('.js-megamenu-back');

      this.currentBlogId = parseInt(element.dataset.currentBlogId, 10) || 0;
      this.currentUrl = element.dataset.currentUrl || '/';
      this.activeIndex = 0; // Will be updated by detectActiveState
      this.lastFocusedSection = null;
      this.isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;

      this.init();
    }

    init() {
      this.detectActiveState();
      this.bindEvents();
      this.handleResize();
      this.initPersonalizedContent();
    }

    /**
     * Detect active state based on current URL and blog ID.
     * This allows the HTML to be cached while active state is determined client-side.
     *
     * Priority:
     * 1. Explicit from_site context
     * 2. Exact page and fragment matching
     * 3. URL prefix matching on all links (more specific match wins)
     * 4. Sub-site link matching by blog ID (fallback)
     */
    detectActiveState() {
      let activeLink = null;
      let activeSectionIndex = 0;
      let isFragmentMatch = false;
      const currentUrl = new URL(window.location.href);
      const allLinks = this.menu.querySelectorAll('.megamenu__link');
      this.currentUrl = currentUrl.pathname;

      // First priority: from_site query parameter → match subsite by slug
      // Explicit context signal takes precedence over URL matching
      // (e.g. /asiantuntijat/erica-wiik/?from_site=fysioterapia should activate the fysioterapia subsite link)
      try {
        const fromSite = new URLSearchParams(window.location.search).get('from_site');
        if (fromSite) {
          const subsiteLinks = this.menu.querySelectorAll('.megamenu__link--subsite[data-site-slug]');
          subsiteLinks.forEach((link) => {
            if (link.dataset.siteSlug === fromSite) {
              activeLink = link;
            }
          });
        }
      } catch (e) {
        // URLSearchParams unavailable
      }

      // Second priority: URL prefix matching on ALL links (including subsites)
      // This ensures specific page links take precedence over general subsite links
      if (!activeLink) {
        let bestMatch = null;
        let bestMatchLength = 0;
        let fragmentMatch = null;

        allLinks.forEach((link) => {
          try {
            const linkUrl = new URL(link.href, currentUrl.href);
            const linkPath = linkUrl.pathname;

            if (!fragmentMatch && currentUrl.hash && linkUrl.origin === currentUrl.origin &&
                linkPath === currentUrl.pathname && linkUrl.hash === currentUrl.hash) {
              fragmentMatch = link;
            }

            // Check if current URL starts with link path (prefix matching)
            if (linkPath && linkPath !== '/' && this.currentUrl.startsWith(linkPath)) {
              // Prefer longer matches (more specific)
              if (linkPath.length > bestMatchLength) {
                bestMatch = link;
                bestMatchLength = linkPath.length;
              }
            }
          } catch (e) {
            // Invalid URL, skip
          }
        });

        activeLink = fragmentMatch || bestMatch;
        isFragmentMatch = !!fragmentMatch;
      }

      // Third priority: Fallback to sub-site link matching by blog ID
      // Only if no URL match was found
      if (!activeLink && this.currentBlogId > 0) {
        const subsiteLinks = this.menu.querySelectorAll('.megamenu__link--subsite[data-blog-id]');
        subsiteLinks.forEach((link) => {
          const blogId = parseInt(link.dataset.blogId, 10);
          if (blogId === this.currentBlogId) {
            activeLink = link;
          }
        });
      }

      allLinks.forEach((link) => {
        link.classList.remove('megamenu__link--active');
        link.removeAttribute('aria-current');
      });

      // If we found an active link, mark it and find its section
      if (activeLink) {
        // Add active class and aria attribute
        activeLink.classList.add('megamenu__link--active');
        activeLink.setAttribute('aria-current', activeLink.classList.contains('megamenu__link--subsite') ? 'true' : (isFragmentMatch ? 'location' : 'page'));

        // Find which section/panel contains this link
        const panel = activeLink.closest('.megamenu__panel');
        if (panel) {
          activeSectionIndex = parseInt(panel.dataset.sectionIndex, 10) || 0;
        }
      }

      // Update the active section (if an active link was found)
      // On mobile: only mark the button as active, do NOT drill in (is-drilled).
      // Drilling on init would hide the sections list when the menu first opens,
      // preventing keyboard users from seeing and navigating between all sections.
      if (activeLink) {
        this.activateSection(activeSectionIndex, false);
        // Store the corresponding section button so goBack() can restore focus
        this.lastFocusedSection = this.sections[activeSectionIndex] || this.sections[0] || null;
      }

      this.activeIndex = activeSectionIndex;
    }

    bindEvents() {
      // Section button clicks
      this.sections.forEach((btn) => {
        btn.addEventListener('click', (e) => this.handleSectionClick(e));
        btn.addEventListener('keydown', (e) => this.handleSectionKeydown(e));
      });

      // Back button clicks
      this.backButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => this.handleBackClick(e));
      });

      // Help toggle buttons (accordion)
      const helpToggles = this.menu.querySelectorAll('.js-megamenu-help-toggle');
      helpToggles.forEach((btn) => {
        btn.addEventListener('click', (e) => this.handleHelpToggle(e));
      });

      // Escape key to go back (mobile) or close
      this.menu.addEventListener('keydown', (e) => this.handleMenuKeydown(e));
      this.menu.addEventListener('click', (e) => this.handleLinkClick(e));

      // Resize handler
      window.addEventListener('resize', () => this.handleResize());
      window.addEventListener('hashchange', () => this.detectActiveState());
    }

    handleLinkClick(e) {
      if (e.defaultPrevented || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) {
        return;
      }

      const link = e.target.closest('a[href]');
      if (!link || !this.menu.contains(link) || link.hasAttribute('download') ||
          (link.target && link.target.toLowerCase() !== '_self')) {
        return;
      }

      const currentUrl = new URL(window.location.href);
      let linkUrl;
      try {
        linkUrl = new URL(link.href, currentUrl.href);
      } catch (error) {
        return;
      }

      if (!linkUrl.hash || linkUrl.origin !== currentUrl.origin ||
          linkUrl.pathname !== currentUrl.pathname || linkUrl.search !== currentUrl.search) {
        return;
      }

      const toggle = document.getElementById('network-menu-toggle');
      if (toggle && toggle.classList.contains('menu-toggle--active')) {
        toggle.click();
      }
    }

    /**
     * Handle help button toggle (accordion)
     */
    handleHelpToggle(e) {
      const btn = e.currentTarget;
      const contentId = btn.getAttribute('aria-controls');
      const content = document.getElementById(contentId);

      if (!content) { return; }

      const isExpanded = btn.getAttribute('aria-expanded') === 'true';

      // Toggle state
      btn.setAttribute('aria-expanded', !isExpanded);
      content.hidden = isExpanded;
    }

    /**
     * Handle section button click
     */
    handleSectionClick(e) {
      const btn = e.currentTarget;
      const index = parseInt(btn.dataset.sectionIndex, 10);

      this.lastFocusedSection = btn;
      this.activateSection(index, true);

      // On mobile, focus the back button after animation
      if (!this.isDesktop) {
        const panel = this.panels[index];
        const backBtn = panel.querySelector('.js-megamenu-back');
        if (backBtn) {
          // Wait for animation to complete
          setTimeout(() => {
            backBtn.focus();
          }, 350);
        }
      }
    }

    /**
     * Handle keyboard navigation on section buttons
     */
    handleSectionKeydown(e) {
      const currentIndex = Array.from(this.sections).indexOf(e.currentTarget);
      let newIndex = currentIndex;

      switch (e.key) {
        case 'Tab':
          // On mobile, Tab from a section button should drill into the panel
          // (same as Enter/click) rather than falling into the panel DOM without animation.
          if (!this.isDesktop && !e.shiftKey) {
            e.preventDefault();
            this.lastFocusedSection = e.currentTarget;
            this.activateSection(currentIndex, true);
            const panel = this.panels[currentIndex];
            const backBtn = panel ? panel.querySelector('.js-megamenu-back') : null;
            if (backBtn) {
              setTimeout(() => backBtn.focus(), 350);
            }
          }
          return;
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          newIndex = (currentIndex + 1) % this.sections.length;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          newIndex = (currentIndex - 1 + this.sections.length) % this.sections.length;
          break;
        case 'Home':
          e.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          newIndex = this.sections.length - 1;
          break;
        case 'Enter':
        case ' ':
          // Let click handler handle activation
          return;
        default:
          return;
      }

      // Move focus to new tab
      this.sections[newIndex].focus();

      // On desktop, also activate the tab
      if (this.isDesktop) {
        this.activateSection(newIndex, true);
      }
    }

    /**
     * Handle back button click (mobile drilldown)
     */
    handleBackClick(e) {
      e.preventDefault();
      this.goBack();
    }

    /**
     * Handle keyboard events on the menu container
     */
    handleMenuKeydown(e) {
      if (e.key === 'Escape') {
        if (!this.isDesktop) {
          // On mobile, go back to section list
          const activePanel = this.menu.querySelector('.megamenu__panel--active');
          if (activePanel) {
            e.preventDefault();
            this.goBack();
          }
        }
      }
    }

    /**
     * Activate a section by index.
     *
     * @param {number}  index         Section index to activate.
     * @param {boolean} userInitiated True when triggered by explicit user interaction.
     *                                Only user-initiated activations drill in on mobile.
     */
    activateSection(index, userInitiated = false) {
      // Update section buttons
      this.sections.forEach((btn, i) => {
        const isActive = i === index;
        btn.classList.toggle('megamenu__section-btn--active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        btn.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      // Update panels
      this.panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('megamenu__panel--active', isActive);

        if (this.isDesktop) {
          panel.hidden = !isActive;
        } else {
          // On mobile, don't use hidden attribute (CSS handles visibility)
          panel.removeAttribute('hidden');
        }
      });

      // On mobile, add drilled state only when user explicitly opens a panel.
      // detectActiveState passes userInitiated=false so the sections list
      // remains visible when the menu first opens.
      if (!this.isDesktop && userInitiated) {
        this.menu.classList.add('is-drilled');
      }

      this.activeIndex = index;
    }

    /**
     * Go back to section list (mobile)
     */
    goBack() {
      // Remove drilled state
      this.menu.classList.remove('is-drilled');

      // Remove active class from current panel
      this.panels.forEach((panel) => {
        panel.classList.remove('megamenu__panel--active');
      });

      // Restore section buttons: make all tabbable again so keyboard users can re-enter
      this.sections.forEach((btn, i) => {
        const isActive = i === this.activeIndex;
        btn.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      // Return focus to the last focused section button
      if (this.lastFocusedSection) {
        setTimeout(() => {
          this.lastFocusedSection.focus();
        }, 350);
      }
    }

    /**
     * Handle window resize
     */
    handleResize() {
      const wasDesktop = this.isDesktop;
      this.isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;

      // If switched between mobile/desktop, reset state
      if (wasDesktop !== this.isDesktop) {
        this.resetState();
      }
    }

    /**
     * Reset menu state after breakpoint change
     */
    resetState() {
      // Always remove drilled state on breakpoint change
      this.menu.classList.remove('is-drilled');

      if (this.isDesktop) {
        // On desktop: show active panel, use hidden attribute
        this.panels.forEach((panel, i) => {
          const isActive = i === this.activeIndex;
          panel.classList.toggle('megamenu__panel--active', isActive);
          panel.hidden = !isActive;
        });
      } else {
        // On mobile: hide all panels initially, remove hidden attributes
        this.panels.forEach((panel) => {
          panel.classList.remove('megamenu__panel--active');
          panel.removeAttribute('hidden');
        });
      }
    }

    /* =========================================================================
       Personalized Content (Sinun Coronaria)
       ========================================================================= */

    /**
     * Initialize personalized content: fetch recently viewed items and set up slider.
     */
    initPersonalizedContent() {
      this.sliderContainer = this.menu.querySelector('.js-megamenu-slider');
      this.recentItemsContainer = this.menu.querySelector('.js-megamenu-recent-items');
      this.sliderIndicatorContainer = this.menu.querySelector('.js-megamenu-slider-indicator');
      this.sliderArrowsContainer = this.menu.querySelector('.js-megamenu-slider-arrows');
      this.sliderInstance = null;

      if (!this.sliderContainer || !this.recentItemsContainer) { return; }

      // Bind remove button handler via event delegation
      var self = this;
      this.menu.querySelector('.js-megamenu-personalized-content').addEventListener('click', function (e) {
        var removeBtn = e.target.closest('.js-megamenu-pcard-remove');
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();
          self.handleCardRemove(removeBtn);
        }
      });

      // Consent placeholder
      this.consentPlaceholder = this.menu.querySelector('.js-megamenu-consent-placeholder');
      this.emptyPlaceholder = this.menu.querySelector('.js-megamenu-empty-placeholder');
      var consentBtn = this.menu.querySelector('.js-megamenu-open-consent');
      if (consentBtn) {
        consentBtn.addEventListener('click', function () {
          if (typeof window.Cookiebot !== 'undefined') {
            window.Cookiebot.renew();
          }
        });
      }

      this.isLoadingCards = false;
      this.refreshPlaceholders();

      // Initialize slider once - it automatically adjusts to DOM changes
      this.initSlider();

      // Load recently viewed items
      this.loadRecentlyViewed();
    }

    /**
     * Refresh both placeholders from ground-truth state.
     * Should be called whenever consent or card state may have changed.
     */
    refreshPlaceholders() {
      var consent = hasPreferencesConsent();
      var hasCards = !!(this.recentItemsContainer &&
        this.recentItemsContainer.querySelector('.megamenu__pcard:not(.megamenu__pcard--skeleton)'));

      if (this.consentPlaceholder) {
        this.consentPlaceholder.hidden = consent;
      }
      if (this.emptyPlaceholder) {
        this.emptyPlaceholder.hidden = !consent || hasCards || this.isLoadingCards;
      }
    }

    /**
     * Show or hide the consent placeholder based on current preferences consent.
     * @deprecated Use refreshPlaceholders() instead.
     */
    updateConsentPlaceholder() {
      this.refreshPlaceholders();
    }

    /**
     * Show or hide the empty-state placeholder.
     * @deprecated Use refreshPlaceholders() instead.
     */
    updateEmptyPlaceholder() {
      this.refreshPlaceholders();
    }

    /**
     * Load recently viewed items from localStorage and fetch their cards.
     */
    loadRecentlyViewed() {
      var items = getRecentlyViewed();

      if (!items.length) {
        this.refreshPlaceholders();
        return;
      }

      var ids = items.map(function (item) {
        return item.id;
      });

      // Show loading skeletons
      this.isLoadingCards = true;
      this.showLoadingSkeletons(ids.length);
      this.refreshPlaceholders();

      // Fetch card HTML from REST API
      var self = this;
      var url = REST_ENDPOINT + '?ids=' + ids.join(',');

      // Use axios if available (it's in the bundle), otherwise fetch
      if (typeof axios !== 'undefined') {
        axios
          .get(url)
          .then(function (response) {
            self.handleCardsResponse(response.data);
          })
          .catch(function () {
            self.isLoadingCards = false;
            self.clearLoadingSkeletons();
            self.refreshPlaceholders();
          });
      } else {
        fetch(url)
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            self.handleCardsResponse(data);
          })
          .catch(function () {
            self.isLoadingCards = false;
            self.clearLoadingSkeletons();
            self.refreshPlaceholders();
          });
      }
    }

    /**
     * Handle the REST API response with card HTML.
     *
     * @param {Object} data Response data with html and count.
     */
    handleCardsResponse(data) {
      this.isLoadingCards = false;
      if (data && data.html) {
        this.recentItemsContainer.innerHTML = data.html;
      } else {
        this.recentItemsContainer.innerHTML = '';
      }
      this.refreshPlaceholders();
    }

    /**
     * Show loading skeleton placeholders.
     *
     * @param {number} count Number of skeletons to show.
     */
    showLoadingSkeletons(count) {
      var html = '';
      for (var i = 0; i < Math.min(count, 4); i++) {
        html += '<div class="megamenu__pcard megamenu__pcard--skeleton">' +
          '<div class="megamenu__pcard-link">' +
          '<div class="megamenu__pcard-thumbnail"></div>' +
          '<div class="megamenu__pcard-text">' +
          '<span class="megamenu__pcard-title"></span>' +
          '<span class="megamenu__pcard-subtitle"></span>' +
          '</div>' +
          '</div>' +
          '</div>';
      }
      this.recentItemsContainer.innerHTML = html;
    }

    /**
     * Clear loading skeletons.
     */
    clearLoadingSkeletons() {
      this.recentItemsContainer.innerHTML = '';
    }

    /**
     * Initialize the overflow slider with plugins.
     */
    initSlider() {
      if (!this.sliderContainer || !window.OverflowSlider) { return; }

      var plugins = [];

      // Scroll indicator plugin
      if (window.OverflowSliderScrollIndicatorPlugin && this.sliderIndicatorContainer) {
        plugins.push(
          window.OverflowSliderScrollIndicatorPlugin({
            container: this.sliderIndicatorContainer,
          })
        );
      }

      // Arrows plugin
      if (window.OverflowSliderArrowsPlugin && this.sliderArrowsContainer) {
        plugins.push(
          window.OverflowSliderArrowsPlugin({
            container: this.sliderArrowsContainer,
            texts: {
              buttonPrevious: 'Edelliset',
              buttonNext: 'Seuraavat',
            },
            icons: {
              prev: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M14 7.33333H4.55333L6.94 4.94L6 4L2 8L6 12L6.94 11.06L4.55333 8.66667H14V7.33333Z" fill="white"/></svg>',
              next: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 7.33333H11.4467L9.06 4.94L10 4L14 8L10 12L9.06 11.06L11.4467 8.66667H2V7.33333Z" fill="white"/></svg>',
            },
          })
        );
      }

      // Drag scrolling plugin
      if (window.OverflowSliderDragScrollingPlugin) {
        plugins.push(window.OverflowSliderDragScrollingPlugin());
      }

      this.sliderInstance = new window.OverflowSlider(
        this.sliderContainer,
        [],
        plugins
      );
    }

    /**
     * Handle remove button click on a personalized card.
     *
     * @param {HTMLElement} removeBtn The clicked remove button.
     */
    handleCardRemove(removeBtn) {
      var card = removeBtn.closest('.megamenu__pcard');
      if (!card) { return; }

      var postId = parseInt(card.dataset.postId, 10);
      if (!postId) { return; }

      // Remove from localStorage
      removeRecentlyViewed(postId);

      // Remove card from DOM with animation (slider reacts to DOM changes automatically)
      card.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
      card.style.opacity = '0';
      card.style.transform = 'scale(0.9)';

      var self = this;
      setTimeout(function () {
        card.remove();
        self.refreshPlaceholders();
      }, 200);
    }
  }

  /**
   * Initialize all megamenus on page
   */
  function initMegamenus() {
    const menus = document.querySelectorAll('.js-megamenu');
    menus.forEach((menu) => {
      if (!menu.megamenuInstance) {
        menu.megamenuInstance = new Megamenu(menu);
      }
    });
  }

  // Initialize on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', initMegamenus);

  // Also initialize if loaded dynamically (for AJAX content)
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    initMegamenus();
  }
})();
